class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

const headerMenuDesktopMedia = window.matchMedia('(min-width: 990px)');

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.summaryToggle = this.mainDetailsToggle.querySelector('summary');
    this.openTimeout = null;
    this.onTransitionEndHandler = null;
    this.closeTransitionTimeout = null;

    this.addEventListener('mouseenter', this.onMouseEnter.bind(this));
    this.addEventListener('mouseleave', this.onMouseLeave.bind(this));
    this.addEventListener('focusin', this.onFocusIn.bind(this));
    // Capture phase so this runs before global.js's onKeyUpEscape (attached to
    // the details element), which strips [open] directly and skips the close
    // animation and the shared open-menu state.
    this.addEventListener('keyup', this.onEscapeKeyUp.bind(this), true);
    this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this));
  }

  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    HeaderMenu.activeInstancesCount = (HeaderMenu.activeInstancesCount || 0) + 1;
    if (HeaderMenu.activeInstancesCount === 1) {
      HeaderMenu.registerHeaderListeners();
    }
  }

  disconnectedCallback() {
    if (super.disconnectedCallback) super.disconnectedCallback();

    HeaderMenu.activeInstancesCount = (HeaderMenu.activeInstancesCount || 0) - 1;
    if (HeaderMenu.activeInstancesCount <= 0) {
      HeaderMenu.removeHeaderListeners();
    }
  }

  onMouseEnter() {
    if (!headerMenuDesktopMedia.matches) return;

    if (HeaderMenu.globalCloseTimeout) {
      clearTimeout(HeaderMenu.globalCloseTimeout);
      HeaderMenu.globalCloseTimeout = null;
    }

    if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.classList.contains('menu-closing')) {
      return;
    }

    // Shorter intent delay while another menu is already open: the user is
    // browsing between panels, not deciding whether to open one.
    const delay = HeaderMenu.activeMenuInstance ? 50 : 120;
    this.openTimeout = setTimeout(() => {
      this.openTimeout = null;
      this.openMenu();
    }, delay);
  }

  onMouseLeave() {
    // Cancel a pending hover-open: without this, sweeping the cursor across
    // several items opens whichever timer fires first — usually not the item
    // the cursor rested on — and that menu then sticks open.
    if (this.openTimeout) {
      clearTimeout(this.openTimeout);
      this.openTimeout = null;
    }
  }

  onFocusIn() {
    if (!headerMenuDesktopMedia.matches) return;

    if (HeaderMenu.globalCloseTimeout) {
      clearTimeout(HeaderMenu.globalCloseTimeout);
      HeaderMenu.globalCloseTimeout = null;
    }
  }

  onSummaryClick(event) {
    if (!headerMenuDesktopMedia.matches) return;

    // The native <details> toggle snaps the panel shut with no animation and
    // leaves the shared open-menu state stale; route through openMenu()/close().
    event.preventDefault();

    if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.classList.contains('menu-closing')) {
      this.close();
    } else {
      this.openMenu();
    }
  }

  onEscapeKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;
    if (!headerMenuDesktopMedia.matches) return;
    if (!this.mainDetailsToggle.hasAttribute('open')) return;

    event.stopPropagation();
    this.close();
    this.summaryToggle.focus();
  }

  openMenu() {
    if (this.openTimeout) {
      clearTimeout(this.openTimeout);
      this.openTimeout = null;
    }
    this.cleanupClosingState();

    // Moving from one open panel to another: swap the content in place
    // instead of replaying the full reveal + stagger. The previous panel
    // closes instantly and the new one only fades its items in (CSS
    // .menu-switching); the transparent header's underlay morphs between
    // the two heights on its own transition.
    const previous = HeaderMenu.activeMenuInstance;
    const switching = Boolean(previous) && previous !== this;
    const fromHeight = switching ? previous.content.offsetHeight : 0;
    HeaderMenu.closeAllMenus(switching, this);
    this.mainDetailsToggle.classList.toggle('menu-switching', switching);

    HeaderMenu.activeMenuInstance = this;
    this.mainDetailsToggle.setAttribute('open', '');
    this.summaryToggle.setAttribute('aria-expanded', 'true');
    if (switching) this.morphHeight(fromHeight);
    this.cacheAllRects();
  }

  /**
   * Panels differ in height. When swapping one for another, grow or shrink
   * the new panel from the previous height instead of snapping, on the same
   * duration/easing the transparent header's underlay uses, so the backdrop
   * and the panel edge move as one surface.
   * @param {number} fromHeight - Rendered height of the panel being replaced.
   */
  morphHeight(fromHeight) {
    this.cleanupHeightMorph();

    const content = this.content;
    const toHeight = content.offsetHeight;
    if (!fromHeight || fromHeight === toHeight) return;

    content.style.transition = 'none';
    content.style.overflow = 'hidden';
    content.style.height = `${fromHeight}px`;
    content.offsetHeight;
    content.style.transition = '';
    content.style.height = `${toHeight}px`;

    this.onHeightMorphEnd = (event) => {
      if (event && event.propertyName !== 'height') return;
      this.cleanupHeightMorph();
    };
    content.addEventListener('transitionend', this.onHeightMorphEnd);
    this.heightMorphTimeout = setTimeout(this.onHeightMorphEnd, 700);
  }

  cleanupHeightMorph() {
    if (this.onHeightMorphEnd) {
      this.content.removeEventListener('transitionend', this.onHeightMorphEnd);
      this.onHeightMorphEnd = null;
    }
    if (this.heightMorphTimeout) {
      clearTimeout(this.heightMorphTimeout);
      this.heightMorphTimeout = null;
    }
    this.content.style.height = '';
    this.content.style.overflow = '';
    this.content.style.transition = '';
  }

  cacheAllRects() {
    const summary = this.querySelector('summary');
    const content = this.querySelector('.mega-menu__content, .header__submenu');
    if (summary && content) {
      this.summaryRect = summary.getBoundingClientRect();
      this.contentRect = content.getBoundingClientRect();
    }

    HeaderMenu.allSummaryRects = [];
    for (const sum of document.querySelectorAll('header-menu summary')) {
      HeaderMenu.allSummaryRects.push(sum.getBoundingClientRect());
    }
  }

  close(immediate = false) {
    if (this.openTimeout) {
      clearTimeout(this.openTimeout);
      this.openTimeout = null;
    }

    // Clear the active instance even when the details was already closed by a
    // path that bypassed this method; otherwise the safe-zone mousemove keeps
    // tracking a closed menu with stale rects.
    if (HeaderMenu.activeMenuInstance === this) {
      HeaderMenu.activeMenuInstance = null;
    }

    if (!this.mainDetailsToggle.hasAttribute('open')) return;

    if (headerMenuDesktopMedia.matches && !immediate) {
      if (this.mainDetailsToggle.classList.contains('menu-closing')) return;

      this.mainDetailsToggle.classList.add('menu-closing');
      this.mainDetailsToggle.classList.remove('menu-switching');

      this.onTransitionEndHandler = (event) => {
        if (
          event.propertyName === 'opacity' ||
          event.propertyName === 'transform' ||
          event.type === 'animationend'
        ) {
          this.cleanupClosingState();
          super.close();
        }
      };

      this.content.addEventListener('transitionend', this.onTransitionEndHandler);
      this.content.addEventListener('animationend', this.onTransitionEndHandler);

      this.closeTransitionTimeout = setTimeout(() => {
        this.cleanupClosingState();
        super.close();
      }, 550);
    } else {
      this.cleanupClosingState();
      super.close();
    }
  }

  cleanupClosingState() {
    if (this.onTransitionEndHandler) {
      this.content.removeEventListener('transitionend', this.onTransitionEndHandler);
      this.content.removeEventListener('animationend', this.onTransitionEndHandler);
      this.onTransitionEndHandler = null;
    }
    if (this.closeTransitionTimeout) {
      clearTimeout(this.closeTransitionTimeout);
      this.closeTransitionTimeout = null;
    }
    this.mainDetailsToggle.classList.remove('menu-closing');
    this.mainDetailsToggle.classList.remove('menu-switching');
    this.cleanupHeightMorph();
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;
    this.updateHeaderBottomPosition();
  }

  // The mega menu's max-height is derived from this variable. Scrolling moves
  // the sticky header (e.g. the announcement bar comes into view), so it is
  // refreshed on scroll/resize while a menu is open, not only on toggle.
  updateHeaderBottomPosition() {
    if (!this.header) return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }

  static isMouseInSafeZone(event) {
    const activeMenu = HeaderMenu.activeMenuInstance;
    if (!activeMenu) return false;

    if (!activeMenu.summaryRect || !activeMenu.contentRect) return false;

    const summaryRect = activeMenu.summaryRect;
    const contentRect = activeMenu.contentRect;
    const mouseX = event.clientX;
    const mouseY = event.clientY;

    const inSummary = (
      mouseX >= summaryRect.left &&
      mouseX <= summaryRect.right &&
      mouseY >= summaryRect.top &&
      mouseY <= summaryRect.bottom
    );

    const inContent = (
      mouseX >= contentRect.left &&
      mouseX <= contentRect.right &&
      mouseY >= contentRect.top &&
      mouseY <= contentRect.bottom
    );

    const cushion = 20;
    const inBridge = (
      mouseX >= (summaryRect.left - cushion) &&
      mouseX <= (summaryRect.right + cushion) &&
      mouseY >= summaryRect.bottom &&
      mouseY <= contentRect.top
    );

    let inAnySummary = false;
    if (HeaderMenu.allSummaryRects) {
      for (const rect of HeaderMenu.allSummaryRects) {
        if (
          mouseX >= rect.left &&
          mouseX <= rect.right &&
          mouseY >= rect.top &&
          mouseY <= rect.bottom
        ) {
          inAnySummary = true;
          break;
        }
      }
    }

    return inSummary || inContent || inBridge || inAnySummary;
  }

  static registerHeaderListeners() {
    if (HeaderMenu.listenersRegistered) return;
    HeaderMenu.listenersRegistered = true;
    HeaderMenu.globalCloseTimeout = null;

    const initListeners = () => {
      HeaderMenu.globalMouseMoveHandler = (event) => {
        if (!headerMenuDesktopMedia.matches) return;

        if (!HeaderMenu.activeMenuInstance) return;

        if (!HeaderMenu.isMouseInSafeZone(event)) {
          if (!HeaderMenu.globalCloseTimeout) {
            HeaderMenu.globalCloseTimeout = setTimeout(() => {
              HeaderMenu.closeAllMenus(false);
            }, 100);
          }
        } else {
          if (HeaderMenu.globalCloseTimeout) {
            clearTimeout(HeaderMenu.globalCloseTimeout);
            HeaderMenu.globalCloseTimeout = null;
          }
        }
      };

      window.addEventListener('mousemove', HeaderMenu.globalMouseMoveHandler);

      // The safe zone compares live cursor coordinates against rects cached at
      // open time; refresh them when scroll or resize moves the header.
      HeaderMenu.refreshRectsHandler = () => {
        if (!HeaderMenu.activeMenuInstance || HeaderMenu.rectRefreshFrame) return;
        HeaderMenu.rectRefreshFrame = requestAnimationFrame(() => {
          HeaderMenu.rectRefreshFrame = null;
          if (HeaderMenu.activeMenuInstance) {
            HeaderMenu.activeMenuInstance.updateHeaderBottomPosition();
            HeaderMenu.activeMenuInstance.cacheAllRects();
          }
        });
      };
      window.addEventListener('scroll', HeaderMenu.refreshRectsHandler, { passive: true });
      window.addEventListener('resize', HeaderMenu.refreshRectsHandler);

      // No mousemove events arrive once the cursor leaves the viewport, so the
      // safe-zone timer can never fire; close explicitly instead.
      HeaderMenu.documentLeaveHandler = () => {
        if (!headerMenuDesktopMedia.matches) return;
        if (!HeaderMenu.activeMenuInstance) return;
        HeaderMenu.closeAllMenus(false);
      };
      document.addEventListener('mouseleave', HeaderMenu.documentLeaveHandler);

      HeaderMenu.globalCloseTriggerHandler = () => {
        if (!headerMenuDesktopMedia.matches) return;
        HeaderMenu.closeAllMenus(false);
      };

      const closeTriggers = document.querySelectorAll('.header__heading, .header__heading-link, .header__icons');
      for (const trigger of closeTriggers) {
        trigger.addEventListener('mouseenter', HeaderMenu.globalCloseTriggerHandler);
      }

      HeaderMenu.scheduleImagePreload();
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initListeners);
    } else {
      initListeners();
    }
  }

  /**
   * Panel images are lazy so phones (where the inline menu is display:none)
   * never fetch them. On desktop the content of a closed <details> is not
   * rendered either, so "lazy" means "not until the menu opens" and the tiles
   * pop in one by one. Upgrade them to eager at low priority once the page
   * has loaded, or immediately when the pointer reaches the nav.
   */
  static scheduleImagePreload() {
    const warm = () => HeaderMenu.preloadMenuImages();
    const idle = () => {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(warm, { timeout: 2500 });
      } else {
        setTimeout(warm, 800);
      }
    };

    if (document.readyState === 'complete') {
      idle();
    } else {
      window.addEventListener('load', idle, { once: true });
    }

    const nav = document.querySelector('.header__inline-menu');
    nav?.addEventListener('pointerenter', warm, { once: true });
    headerMenuDesktopMedia.addEventListener('change', warm);
  }

  static preloadMenuImages() {
    if (HeaderMenu.imagesPreloaded || !headerMenuDesktopMedia.matches) return;
    HeaderMenu.imagesPreloaded = true;

    for (const image of document.querySelectorAll('header-menu img[loading="lazy"]')) {
      image.setAttribute('fetchpriority', 'low');
      image.loading = 'eager';
    }
  }

  static removeHeaderListeners() {
    if (!HeaderMenu.listenersRegistered) return;
    HeaderMenu.listenersRegistered = false;

    if (HeaderMenu.globalMouseMoveHandler) {
      window.removeEventListener('mousemove', HeaderMenu.globalMouseMoveHandler);
      HeaderMenu.globalMouseMoveHandler = null;
    }

    if (HeaderMenu.refreshRectsHandler) {
      window.removeEventListener('scroll', HeaderMenu.refreshRectsHandler);
      window.removeEventListener('resize', HeaderMenu.refreshRectsHandler);
      HeaderMenu.refreshRectsHandler = null;
    }

    if (HeaderMenu.rectRefreshFrame) {
      cancelAnimationFrame(HeaderMenu.rectRefreshFrame);
      HeaderMenu.rectRefreshFrame = null;
    }

    if (HeaderMenu.documentLeaveHandler) {
      document.removeEventListener('mouseleave', HeaderMenu.documentLeaveHandler);
      HeaderMenu.documentLeaveHandler = null;
    }

    if (HeaderMenu.globalCloseTriggerHandler) {
      const closeTriggers = document.querySelectorAll('.header__heading, .header__heading-link, .header__icons');
      for (const trigger of closeTriggers) {
        trigger.removeEventListener('mouseenter', HeaderMenu.globalCloseTriggerHandler);
      }
      HeaderMenu.globalCloseTriggerHandler = null;
    }
  }

  static closeAllMenus(immediate = false, excludeMenu = null) {
    HeaderMenu.allSummaryRects = null;
    for (const menu of document.querySelectorAll('header-menu')) {
      if (menu === excludeMenu) continue;
      menu.close(immediate);
      if (menu.openTimeout) {
        clearTimeout(menu.openTimeout);
        menu.openTimeout = null;
      }
    }
  }
}

customElements.define('header-menu', HeaderMenu);
